import * as React from 'react'
import { Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select as IconSelect,
  SelectContent as IconSelectContent,
  SelectItem as IconSelectItem,
  SelectTrigger as IconSelectTrigger,
  SelectValue as IconSelectValue,
} from '@/components/ui/select'
import { SheetFooter } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import {
  isValidToolParameterName,
  parametersToArgsSchema,
} from '@/features/tools/schema'
import type { AppTool, AppToolWrite } from '@/features/tools/types'
import { TOOL_ICON_OPTIONS } from '@/features/tools/icons'
import { draftFromOrgTool, type ToolDraft } from './toolDraft'
import { ParameterListBuilder } from './ParameterListBuilder'
import { ToggleAccordionField } from './ToggleAccordionField'

export interface ToolBuilderFormProps {
  orgTool?: AppTool
  onSubmit: (tool: AppToolWrite) => Promise<void>
  submitLabel?: string
  onDelete?: () => void
  onCancel?: () => void
}

export function ToolBuilderForm({
  orgTool,
  onSubmit,
  submitLabel,
  onDelete,
  onCancel,
}: ToolBuilderFormProps) {
  const [draft, setDraft] = React.useState<ToolDraft>(() =>
    draftFromOrgTool(orgTool)
  )
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const target = orgTool?.execution_target ?? 'webhook'
  const careforceManaged = target === 'builtin' || orgTool?.is_system
  const showDescription = !orgTool?.is_system && target === 'webhook'
  const showParameters = target === 'webhook'
  const showWebhook = target === 'webhook' && !orgTool?.is_system

  const update = (patch: Partial<ToolDraft>) =>
    setDraft((current) => ({ ...current, ...patch }))

  const hasInvalidParameterName =
    showParameters &&
    draft.parameters.some((parameter) => {
      const trimmed = parameter.name.trim()
      return trimmed.length > 0 && !isValidToolParameterName(trimmed)
    })
  const canSubmit =
    (!showDescription || draft.description.trim().length > 0) &&
    (!showWebhook || draft.webhook_url.trim().length > 0) &&
    !hasInvalidParameterName &&
    draft.name.trim().length > 0 &&
    (!draft.angelica_enabled ||
      draft.angelica_instructions.trim().length > 0) &&
    (!draft.david_enabled || draft.david_instructions.trim().length > 0)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        name: draft.name.trim(),
        ...(showDescription ? { description: draft.description.trim() } : {}),
        angelica_instructions: draft.angelica_instructions.trim(),
        angelica_enabled: draft.angelica_enabled,
        david_instructions: draft.david_instructions.trim(),
        icon: draft.icon,
        ...(showParameters
          ? { args_schema: parametersToArgsSchema(draft.parameters) }
          : {}),
        ...(showWebhook
          ? {
              webhook_url: draft.webhook_url.trim(),
              webhook_auth_header: draft.webhook_auth_header.trim() || null,
              ...(draft.webhook_auth_secret
                ? { webhook_auth_secret: draft.webhook_auth_secret }
                : {}),
            }
          : {}),
        david_enabled: draft.david_enabled,
        enabled: draft.enabled,
      })
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to save tool'
      )
      return
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={
        onCancel ? 'flex min-h-0 flex-1 flex-col' : 'flex flex-col gap-4'
      }>
      <div
        className={
          onCancel
            ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4'
            : 'flex flex-col gap-4'
        }>
        {careforceManaged ? (
          <div>
            <Badge variant="outline">Managed by Careforce</Badge>
          </div>
        ) : null}
        <div className="flex gap-2">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="tool-name">Name</Label>
            <Input
              id="tool-name"
              placeholder="Check order status"
              value={draft.name}
              onChange={(event) => update({ name: event.target.value })}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <Label>Icon</Label>
            <IconSelect
              value={draft.icon}
              onValueChange={(icon) => update({ icon })}>
              <IconSelectTrigger aria-label="Icon" className="w-full">
                <IconSelectValue />
              </IconSelectTrigger>
              <IconSelectContent>
                {TOOL_ICON_OPTIONS.map(({ value, label, Icon }) => (
                  <IconSelectItem key={value} value={value}>
                    <Icon className="size-4" />
                    {label}
                  </IconSelectItem>
                ))}
              </IconSelectContent>
            </IconSelect>
          </div>
        </div>
        {showDescription && (
          <div className="flex flex-col gap-2">
            <Label>Description</Label>
            <Textarea
              aria-label="Description"
              placeholder="What this tool does (model-facing)."
              value={draft.description}
              onChange={(event) => update({ description: event.target.value })}
              rows={2}
            />
          </div>
        )}
        <ToggleAccordionField
          label="Enabled for Angelica"
          checked={draft.angelica_enabled}
          onCheckedChange={(checked) => update({ angelica_enabled: checked })}>
          <Textarea
            aria-label="Instructions"
            placeholder="Guidance for when/how to use this tool during a live call."
            value={draft.angelica_instructions}
            onChange={(event) =>
              update({ angelica_instructions: event.target.value })
            }
            rows={3}
          />
        </ToggleAccordionField>
        <ToggleAccordionField
          label="Enabled for David"
          checked={draft.david_enabled}
          onCheckedChange={(checked) => update({ david_enabled: checked })}>
          <Textarea
            aria-label="David instructions"
            placeholder="Guidance for when/how David should use this tool."
            value={draft.david_instructions}
            onChange={(event) =>
              update({ david_instructions: event.target.value })
            }
            rows={3}
          />
        </ToggleAccordionField>
        {showParameters ? (
          <div className="flex flex-col gap-2">
            <Label>Parameters</Label>
            <ParameterListBuilder
              parameters={draft.parameters}
              onChange={(parameters) => update({ parameters })}
            />
          </div>
        ) : null}
        {showWebhook ? (
          <div className="flex flex-col gap-2">
            <Label>Webhook</Label>
            <div className="flex flex-col gap-2">
              <Input
                aria-label="Webhook URL"
                placeholder="https://example.com/tools/my-tool"
                value={draft.webhook_url}
                onChange={(event) =>
                  update({ webhook_url: event.target.value })
                }
              />
              <Input
                aria-label="Webhook auth header name"
                placeholder="Auth header name (e.g. X-Api-Key)"
                value={draft.webhook_auth_header}
                onChange={(event) =>
                  update({ webhook_auth_header: event.target.value })
                }
              />
              <Input
                aria-label="Webhook auth secret"
                type="password"
                placeholder={
                  orgTool?.execution_target === 'webhook' &&
                  orgTool.has_webhook_auth_secret
                    ? 'Secret set — leave blank to keep it'
                    : 'Auth secret value'
                }
                value={draft.webhook_auth_secret}
                onChange={(event) =>
                  update({ webhook_auth_secret: event.target.value })
                }
              />
            </div>
          </div>
        ) : null}
      </div>
      <SheetFooter
        className={
          onCancel ? 'shrink-0 border-t border-border bg-neutral-ui-bg' : 'p-0'
        }>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          {onDelete ? (
            <Button
              type="button"
              variant="destructive"
              aria-label={`Delete ${orgTool?.name ?? 'tool'}`}
              disabled={submitting}
              onClick={onDelete}>
              <Trash2 className="size-4" /> Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex justify-end gap-2">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={!canSubmit || submitting}>
              {submitting ? 'Saving…' : (submitLabel ?? 'Save')}
            </Button>
          </div>
        </div>
      </SheetFooter>
    </form>
  )
}
